import { useMemo } from "react";
import {
  BriefcaseIcon,
  DollarSignIcon,
  GlobeIcon,
  MessageCircleIcon,
  SearchIcon,
  SendIcon,
  TargetIcon,
  TimerIcon,
} from "lucide-react";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetStafferTrophies } from "@/hooks/queries/useGetStafferTrophies";
import { dates } from "@/lib/dates";
import { trophies as trophyFormatting } from "@/lib/trophies";
import { cn } from "@/lib/utils";

import { LeaderboardUser, StafferTrophyLeader, TrophyAwardType } from "@/types";

const awards = [
  {
    icon: DollarSignIcon,
    label: "Revenue leader",
    type: TrophyAwardType.REVENUE,
  },
  {
    icon: TimerIcon,
    label: "Fastest placement",
    type: TrophyAwardType.FASTEST_PLACEMENT,
  },
  {
    icon: BriefcaseIcon,
    label: "Internal Placements",
    type: TrophyAwardType.INTERNAL_PLACEMENTS,
  },
  {
    icon: TargetIcon,
    label: "Placement pro",
    type: TrophyAwardType.PLACEMENTS,
  },
  {
    icon: SearchIcon,
    label: "Screening leader",
    type: TrophyAwardType.SCREENS,
  },
  {
    icon: SendIcon,
    label: "Submission leader",
    type: TrophyAwardType.SUBMITTALS,
  },
  {
    icon: MessageCircleIcon,
    label: "Interview leader",
    type: TrophyAwardType.INTERVIEWS,
  },
  {
    icon: GlobeIcon,
    label: "Most versatile",
    type: TrophyAwardType.MOST_VERSATILE,
  },
];

export const TrophyCaseTable = ({
  leaderboardUsers,
}: {
  leaderboardUsers: LeaderboardUser[];
}) => {
  /* Database CRUD Operations */
  // Read
  const { data, isError, isPending } = useGetStafferTrophies();

  /* React Hooks */
  const users = useMemo(() => {
    const usersById = new Map<
      number,
      Pick<StafferTrophyLeader, "firstName" | "lastName" | "userId">
    >();
    leaderboardUsers.forEach((user) => {
      usersById.set(user.userId, user);
    });
    data?.awards.forEach((award) => {
      award.leaders.forEach((leader) => {
        usersById.set(leader.userId, leader);
      });
    });
    const rankCountByUserId = new Map<number, number>();
    data?.awards.forEach((award) => {
      award.leaders.forEach((leader) => {
        const rankCount = rankCountByUserId.get(leader.userId);
        if (rankCount === undefined) {
          rankCountByUserId.set(leader.userId, 1);
        } else {
          rankCountByUserId.set(leader.userId, rankCount + 1);
        }
      });
    });
    const getRankCount = (userId: number) => {
      const rankCount = rankCountByUserId.get(userId);
      if (rankCount === undefined) return 0;
      return rankCount;
    };
    return Array.from(usersById.values()).sort((a, b) => {
      const rankDifference = getRankCount(b.userId) - getRankCount(a.userId);
      if (rankDifference !== 0) return rankDifference;
      return a.firstName.localeCompare(b.firstName);
    });
  }, [data, leaderboardUsers]);

  /* Helpers */
  const getLeaders = (
    userId: number,
    type: TrophyAwardType,
  ): StafferTrophyLeader[] => {
    const leaders = data?.awards
      .find((award) => award.type === type)
      ?.leaders.filter((leader) => leader.userId === userId);
    return leaders ?? [];
  };

  const getRankLabel = (rank: number) => {
    if (rank === 1) return "1st";
    if (rank === 2) return "2nd";
    if (rank === 3) return "3rd";
    return `${String(rank)}th`;
  };

  const getRankClassName = (rank: number) => {
    if (rank === 1) return "bg-amber-100 text-amber-700";
    if (rank === 2) return "bg-slate-100 text-slate-600";
    if (rank === 3) return "bg-orange-100 text-orange-700";
    return "bg-slate-50 text-slate-500";
  };

  /* Render */
  const renderLeaderBadge = (
    leader: StafferTrophyLeader,
    type: TrophyAwardType,
  ) => (
    <Tooltip>
      <TooltipTrigger
        className={cn(
          "mx-auto flex h-7 w-7 cursor-default items-center justify-center rounded-full",
          getRankClassName(leader.rank),
        )}
      >
        {getRankLabel(leader.rank)}
      </TooltipTrigger>
      <TooltipContent>
        <Column>
          <Text className="text-sm font-semibold text-slate-800">
            {trophyFormatting.value(type, leader.value, leader.unit)}
          </Text>
          <Text className="text-xs font-normal text-slate-500">
            {dates.formatIsoDateForUser(leader.achievedAt)}
          </Text>
        </Column>
      </TooltipContent>
    </Tooltip>
  );

  const renderLeaders = (
    leaders: StafferTrophyLeader[],
    type: TrophyAwardType,
  ) => {
    if (leaders.length === 0) return "-";
    return (
      <Row className="justify-center gap-1">
        {leaders.map((leader) => (
          <Column key={leader.rank}>{renderLeaderBadge(leader, type)}</Column>
        ))}
      </Row>
    );
  };

  const render = () => {
    if (isPending) return <LoadingIndicator variant="medium" />;
    if (isError) {
      return <ErrorText errorMessage="Unable to load trophy standings." />;
    }
    return (
      <TooltipProvider delayDuration={100}>
        <Column className="gap-5">
          <Text className="text-xs font-semibold uppercase leading-tight tracking-wider text-slate-800">
            Current Standings
          </Text>
          <div className="max-h-[1200px] overflow-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
              <TableHeader className="sticky top-0 z-20 bg-slate-50 [&_th]:border-b [&_th]:border-slate-200">
                <TableRow className="border-none">
                  <TableHead className="whitespace-nowrap">
                    <Text variant="header">Staffer</Text>
                  </TableHead>
                  {awards.map((award) => {
                    const Icon = award.icon;
                    return (
                      <Tooltip key={award.label}>
                        <TooltipTrigger asChild>
                          <TableHead className="whitespace-nowrap">
                            <Icon className="mx-auto h-4 w-4 text-slate-900" />
                          </TableHead>
                        </TooltipTrigger>
                        <TooltipContent>
                          <Text className="text-sm font-medium text-slate-700">
                            {award.label}
                          </Text>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.userId}>
                    <TableCell className="whitespace-nowrap font-medium">
                      {user.firstName} {user.lastName}
                    </TableCell>
                    {awards.map((award) => (
                      <TableCell
                        key={award.label}
                        className="whitespace-nowrap text-center"
                      >
                        {renderLeaders(
                          getLeaders(user.userId, award.type),
                          award.type,
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        </Column>
      </TooltipProvider>
    );
  };

  return render();
};
